import React, { useState } from "react";
import "./dmsComparison.css";

import {
  FiX,
  FiCheck,
  FiArrowRight,
  FiFileText,
  FiClock,
  FiPackage,
  FiLink,
  FiBarChart2,
  FiZap,
  FiShield,
  FiDatabase,
  FiTrendingUp,
} from "react-icons/fi";

import DmsBookingModal from "./dmsBookingModal";

const withoutDms = [
  {
    icon: FiFileText,
    title: "Excel & Manual Processes",
    text: "Distribution activities rely on spreadsheets, manual entries and disconnected operational processes.",
  },
  {
    icon: FiClock,
    title: "Delayed Sales Data",
    text: "Sales information reaches management late, making it harder to respond quickly to market changes.",
  },
  {
    icon: FiPackage,
    title: "Stock Uncertainty",
    text: "Limited visibility into distributor and channel stock makes availability and stock planning difficult.",
  },
  {
    icon: FiFileText,
    title: "Manual Claims",
    text: "Claims, reconciliation and settlement activities require manual coordination and follow-up.",
  },
  {
    icon: FiLink,
    title: "Disconnected Systems",
    text: "Orders, inventory, sales and channel operations remain spread across different systems and processes.",
  },
  {
    icon: FiBarChart2,
    title: "Limited Visibility",
    text: "Teams have limited visibility across distributors, retailers, sales activity and channel performance.",
  },
  {
    icon: FiClock,
    title: "Slow Decisions",
    text: "Delayed and fragmented information makes business decisions slower and more dependent on manual reporting.",
  },
];

const withDms = [
  {
    icon: FiFileText,
    title: "Digital Orders",
    text: "Digitize order management and connect booking, processing, dispatch, delivery and invoicing.",
  },
  {
    icon: FiTrendingUp,
    title: "Real-Time Sales",
    text: "Gain visibility into primary and secondary sales, product movement and channel performance.",
  },
  {
    icon: FiPackage,
    title: "Distributor Stock Visibility",
    text: "Monitor distributor and warehouse inventory with better visibility into stock availability and movement.",
  },
  {
    icon: FiZap,
    title: "Automated Workflows",
    text: "Connect distribution activities through structured workflows that reduce manual work and improve execution.",
  },
  {
    icon: FiDatabase,
    title: "Centralized Claims",
    text: "Manage claims through creation, verification, approvals, settlement, history and reporting.",
  },
  {
    icon: FiLink,
    title: "Connected Distribution",
    text: "Bring manufacturers, warehouses, distributors, sales teams and retailers into one connected ecosystem.",
  },
  {
    icon: FiBarChart2,
    title: "Live Dashboards",
    text: "Use connected dashboards and reports to support faster, data-driven distribution decisions.",
  },
];

const DmsComparison = () => {
  const [showDmsModal, setShowDmsModal] = useState(false);

  return (
    <>
      <section className="dms-comparison-section">
        <div className="dms-comparison-glow dms-comparison-glow-one"></div>
        <div className="dms-comparison-glow dms-comparison-glow-two"></div>

        <div className="container">
          <div
            className="dms-comparison-heading"
            data-aos="fade-up"
            data-aos-duration="850"
          >
            <span className="dms-comparison-label">THE DMS DIFFERENCE</span>

            <h2>
              From Manual Distribution
              <br />
              to <span>Connected Operations</span>
            </h2>

            <p>
              See how Techcognics DMS transforms fragmented distribution
              processes into a connected, visible and data-driven operation.
            </p>
          </div>

          <div className="dms-comparison-wrapper">
            <div
              className="dms-comparison-panel dms-comparison-without"
              data-aos="fade-right"
              data-aos-duration="900"
            >
              <div className="dms-comparison-panel-header">
                <div className="dms-comparison-panel-title">
                  <span className="dms-comparison-panel-icon">
                    <FiX />
                  </span>

                  <div>
                    <small>WITHOUT DMS</small>
                    <h3>Disconnected Distribution</h3>
                  </div>
                </div>

                <span className="dms-comparison-panel-badge">MANUAL</span>
              </div>

              <div className="dms-comparison-list">
                {withoutDms.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      className="dms-comparison-item"
                      key={item.title}
                      data-aos="fade-up"
                      data-aos-duration="650"
                      data-aos-delay={index * 70}
                    >
                      <div className="dms-comparison-item-icon">
                        <Icon />
                      </div>

                      <div className="dms-comparison-item-content">
                        <h4>{item.title}</h4>
                        <p>{item.text}</p>
                      </div>

                      <span className="dms-comparison-cross">
                        <FiX />
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              className="dms-comparison-center"
              data-aos="zoom-in"
              data-aos-duration="800"
            >
              <div className="dms-comparison-center-line"></div>

              <div className="dms-comparison-center-icon">
                <FiArrowRight />
              </div>

              <span>TRANSFORM</span>

              <strong>
                TECHCOGNICS
                <br />
                DMS
              </strong>

              <div className="dms-comparison-center-line"></div>
            </div>

            <div
              className="dms-comparison-panel dms-comparison-with"
              data-aos="fade-left"
              data-aos-duration="900"
            >
              <div className="dms-comparison-panel-header">
                <div className="dms-comparison-panel-title">
                  <span className="dms-comparison-panel-icon">
                    <FiCheck />
                  </span>

                  <div>
                    <small>WITH TECHCOGNICS DMS</small>
                    <h3>Connected Distribution</h3>
                  </div>
                </div>

                <span className="dms-comparison-panel-badge">CONNECTED</span>
              </div>

              <div className="dms-comparison-list">
                {withDms.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      className="dms-comparison-item"
                      key={item.title}
                      data-aos="fade-up"
                      data-aos-duration="650"
                      data-aos-delay={index * 70}
                    >
                      <div className="dms-comparison-item-icon">
                        <Icon />
                      </div>

                      <div className="dms-comparison-item-content">
                        <h4>{item.title}</h4>
                        <p>{item.text}</p>
                      </div>

                      <span className="dms-comparison-check">
                        <FiCheck />
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div
            className="dms-comparison-result"
            data-aos="fade-up"
            data-aos-duration="750"
          >
            <div className="dms-comparison-result-icon">
              <FiShield />
            </div>

            <div className="dms-comparison-result-content">
              <strong>One connected distribution network.</strong>

              <span>
                Better visibility across sales, inventory, orders and channel
                operations.
              </span>
            </div>

            <div className="dms-comparison-result-arrow">
              <FiArrowRight />
            </div>
          </div>

          <div
            className="dms-comparison-cta"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-delay="150"
          >
            <button
              type="button"
              className="dms-comparison-cta-btn"
              onClick={() => setShowDmsModal(true)}
            >
              See the DMS Difference
              <span>
                <FiArrowRight />
              </span>
            </button>
          </div>
        </div>
      </section>

      <DmsBookingModal
        show={showDmsModal}
        onClose={() => setShowDmsModal(false)}
      />
    </>
  );
};

export default DmsComparison;
